import React, { useEffect, useRef, useState } from 'react';
import { Container, Grid, Box, Typography } from '@mui/material';
import { motion, useAnimation } from 'framer-motion';
import './stack.css';
import StackImage from './StackImage';

const Stack = () => {
  const controls = useAnimation();
  const sectionRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [toggle, setToggle] = useState(0); 

  // Intersection Observer to check if the section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  // Trigger animations when in view
  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  const handleChange = (index) => {
    setToggle(index);
  };

  // Animation Variants
  const leftColumnVariants = {
    hidden: { opacity: 0, x: '-100vw' },
    visible: { opacity: 1, x: 0, transition: { duration: 1, ease: 'easeOut' } },
  };

  const rightColumnVariants = {
    hidden: { opacity: 0, x: '100vw' },
    visible: { opacity: 1, x: 0, transition: { duration: 1, ease: 'easeOut' } },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, delay: i * 0.1 },
    }),
  };

  const ourStackData = {
    frontendData: [
      { id: 1, name: 'Figma', image: `${StackImage.figma}` },
      { id: 2, name: 'HTML 5', image: `${StackImage.html}` },
      { id: 3, name: 'CSS', image: `${StackImage.css}` },
      { id: 4, name: 'Bootstrap', image: `${StackImage.bootstrap}` },
      { id: 5, name: 'Angular.js', image: `${StackImage.angularjs}` },
      { id: 6, name: 'React', image: `${StackImage.react}`},
      { id: 7, name: 'Tailwind', image: `${StackImage.tailwind}` },
      { id: 8, name: 'Material UI', image: `${StackImage.materialui}` },
      { id: 9, name: 'Next.js', image: `${StackImage.nextjs}` },
    ],
    backendData: [
      { id: 1, name: 'JavaScript', image: 'https://suffescom.co/assets/images/java-script-tech-icon.webp' },
      { id: 2, name: 'Node.js', image: 'https://suffescom.co/assets/images/nodejs-tech-icon.webp' },
      { id: 3, name: 'Socket.io', image: 'https://suffescom.co/assets/images/socket-io-tech-icon.webp' },
      { id: 4, name: 'Redis', image: 'https://suffescom.co/assets/images/redis-tech-icon.webp' },
      { id: 5, name: 'MySQL', image: 'https://suffescom.co/assets/images/mysql-tech-icon.webp' },
      { id: 6, name: 'MongoDB', image: 'https://suffescom.co/assets/images/mongo-db-tech-icon.webp' },
      { id: 7, name: 'Laravel', image: 'https://suffescom.co/assets/images/larvel-tech-icon.webp' },
      { id: 8, name: 'PHP', image: 'https://suffescom.co/assets/images/php-tech-icon.webp' },
      { id: 9, name: 'Codeigniter', image: 'https://suffescom.co/assets/images/codeigniter-tech-icon.webp' },
    ],
    blochainData: [
      { id: 1, name: 'Polygon', image: 'https://suffescom.co/assets/images/polygon-tech-icon.webp' },
      { id: 2, name: 'Binance Smartchain', image: 'https://suffescom.co/assets/images/binance-smartchain-tech-icon.webp' },
      { id: 3, name: 'Avalanche', image: 'https://suffescom.co/assets/images/avalanche-tech-icon.webp' },
      { id: 4, name: 'Aptos', image: 'https://suffescom.co/assets/images/aptos-tech-icon.webp' },
      { id: 5, name: 'Remix Blockchain', image: 'https://suffescom.co/assets/images/remix-blockchain-tech-icon.webp' },
      { id: 6, name: 'Web 3.0', image: 'https://suffescom.co/assets/images/web-3-tech-icon.webp' },
      { id: 7, name: 'Solidity', image: 'https://suffescom.co/assets/images/solidity-tech-icon.webp' },
      { id: 8, name: 'Ethereum', image: 'https://suffescom.co/assets/images/ethereum-tech-icon.webp' },
      { id: 9, name: 'Truffle', image: 'https://suffescom.co/assets/images/truffle-tech-icon.webp' },
    ],
    gameData: [
      { id: 1, name: 'Unity', image: 'https://suffescom.co/assets/images/unity-tech-icon.webp' },
      { id: 2, name: 'Unreal', image: 'https://suffescom.co/assets/images/unreal-tech-icon.webp' },
      { id: 3, name: 'Maya', image: 'https://suffescom.co/assets/images/maya-tech-icon.webp' },
      { id: 4, name: 'Substance Painter', image: 'https://suffescom.co/assets/images/substance-tech-stack.webp' },
      { id: 5, name: 'Adobe Illustrator', image: 'https://suffescom.co/assets/images/adobe-illustrator-tech-stack.webp' },
      { id: 6, name: 'Blender', image: 'https://suffescom.co/assets/images/blender-tech-icon.webp' },
      { id: 7, name: 'After Effects', image: 'https://suffescom.co/assets/images/after_effects-tech-icon.webp' },
      
    ],
    mobileDevData: [
      { id: 1, name: 'React Native', image: 'https://suffescom.co/assets/images/react_logo-tech-icon.webp' },
      { id: 2, name: 'Flutter', image: 'https://suffescom.co/assets/images/flutter-tech-icon.webp' },
      { id: 3, name: 'Ionic', image: 'https://suffescom.co/assets/images/ionic-tech-icon.webp' },
    ],
  };

  const tabMenu =[
    {id:1 , name : 'Front End Development' , image : 'https://suffescom.co/assets/images/front-end-development.svg'},
    {id:2 ,  name : 'Back End Development' , image : 'https://suffescom.co/assets/images/backend-development.svg'},
    {id:3 ,  name : 'Blockchain Development' , image : 'https://suffescom.co/assets/images/blockchain-development.svg'},
    {id:4 ,  name : 'Game Development' , image : 'https://suffescom.co/assets/images/game-development.svg'},
    {id:5 ,  name : 'Mobile App Development' ,image : 'https://suffescom.co/assets/images/mobile-app-development.svg'},
  ]

  const dataToDisplay =
      toggle === 0
    ? ourStackData.frontendData
    : toggle === 1
    ? ourStackData.backendData
    : toggle === 2
    ? ourStackData.blochainData
    : toggle === 3
    ? ourStackData.gameData
    : toggle === 4
    ? ourStackData.mobileDevData
    : [];

  return (
    <>
      <Typography
        variant="h4"
        align="center"
        sx={{
          marginTop: '100px',
          fontWeight: 'bold',
          color: '#333',
        }}
      >
        Our Tech Stacks
      </Typography>
      <Container
        ref={sectionRef}
        sx={{
          background: '#fff',
          padding: '40px',
          borderRadius: '16px',
          marginTop: '50px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Grid container spacing={4}>
          {/* Left Column */}
          <Grid item xs={12} md={5}>
          <motion.div
            variants={leftColumnVariants}
            initial="hidden"
            animate={controls}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' }, // Horizontal on small screens, vertical on larger screens
                gap: '15px',
                overflowX: { xs: 'auto', md: 'unset' }, // Enable horizontal scrolling on small screens
                paddingBottom: { xs: '10px', md: '0' },
                '&::-webkit-scrollbar': {
                  display: 'none', // Hide scrollbar for a cleaner UI
                },
                msOverflowStyle: 'none', // Hide scrollbar for IE/Edge
                scrollbarWidth: 'none', // Hide scrollbar for Firefox
              }}
            >
              {tabMenu.map((data, index) => (
                <Box
                  key={index}
                  onClick={() => handleChange(index)}
                  sx={{
                    minWidth: { xs: '259px', md: 'unset' },
                    background: index === toggle ? 'linear-gradient(to right, #2D0B5A, #C106A3)' : '#f1f1f1',
                    padding: '15px',
                    borderRadius: '50px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={`${data.image}`}
                    alt={data.name}
                    style={{
                      background: '#fff',
                      padding: '10px',
                      borderRadius: '50%',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      width: '50px',
                      height: '50px',
                    }}
                    className="stack-left-menu-img"
                  />
                  <Typography
                      variant="h6"
                      sx={{
                        color: index === toggle ? '#fff' : '#333',
                        fontWeight: 'bold',
                        fontSize: { xs: '1rem' , md : '1.2rem'},
                        display: 'flex', 
                        justifyContent: { xs: 'center', md: 'flex-start' },  
                        textAlign: { xs: 'center', md: 'left' },
                        width: '100%',
                        marginLeft: {md:'5px'}
                      }}
                    >
                    {data.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </motion.div>
          </Grid>


          
          {/* Right Column */}
          <Grid item xs={12} md={7}>
            <motion.div
              key={toggle}
              variants={rightColumnVariants}
              initial="hidden"
              animate="visible"
            >
              <Grid container spacing={3}>
                {dataToDisplay.map((item, index) => (
                  <Grid item xs={6} sm={4} key={item.id}>
                    <motion.div
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      custom={index}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          backgroundColor: '#fff',
                          padding: '20px',
                          borderRadius: '12px',
                          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                          '&:hover': {
                            transform: 'translateY(-10px)',
                            boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.15)',
                          },
                        }}
                      >
                        <img
                          src={item.image}
                          alt={item.name}
                          style={{ width: '60px', height: '60px', marginBottom: '15px' }}
                        />
                        <Typography variant="h6">{item.name}</Typography>
                      </Box>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Grid>

        </Grid>
      </Container>
    </>
  );
};

export default Stack;
